import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { buildChargebacksView, type ChargebacksView } from './chargebacksModel';
import type {
  BalanceLedgerEntry,
  Transaction,
  User,
} from '../dataProvider/types';

/**
 * The Chargebacks worklist, read through the aggregation seam
 * (`buildChargebacksView`), NOT computed in the component. For v1 it pulls the raw
 * transactions, users and reversal ledger from the mock provider and aggregates
 * client-side; the real-data migration replaces the fetch-and-aggregate here with a
 * server-side endpoint and the surface does not change. Same seam idiom as
 * useReferrals.
 *
 * OPERATIONAL, SWITCHER-SCOPED (D-chargebacks): scope comes from the region context
 * (UK | US | GLOBAL), exactly like the cashout queue, NOT a local investigative
 * facet. A single-region reviewer sees only their market's disputes.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useChargebacks = (): {
  view: ChargebacksView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['chargebacks', region],
    queryFn: async () => {
      const [transactions, users, ledger] = await Promise.all([
        dataProvider.getList('transactions', ALL),
        dataProvider.getList('users', ALL),
        dataProvider.getList('balanceLedger', ALL),
      ]);
      return buildChargebacksView(
        {
          transactions: transactions.data as unknown as Transaction[],
          users: users.data as unknown as User[],
          ledger: ledger.data as unknown as BalanceLedgerEntry[],
        },
        { scope: region },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
